
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { verifyStripePayment } from '@/utils/stripeUtils';
import { trackConversion, resetReferralTracking, getReferralId, ensureRewardfulInitialized } from '@/utils/rewardfulUtils';

interface UsePaymentVerificationProps {
  setEmail: (email: string) => void;
  setHasPaid: (hasPaid: boolean) => void;
  setPaymentError: (error: string) => void;
}

export const usePaymentVerification = ({
  setEmail,
  setHasPaid,
  setPaymentError
}: UsePaymentVerificationProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [verifiedSessionId, setVerifiedSessionId] = useState('');
  const [verificationCount, setVerificationCount] = useState(0);
  
  // Track if a conversion has already been reported for this session ID
  useEffect(() => {
    if (verifiedSessionId) {
      sessionStorage.setItem(`verified_session_${verifiedSessionId}`, 'true');
    }
  }, [verifiedSessionId]);
  
  const verifyPaymentSession = async (sid: string, emailAddress: string) => {
    if (!sid) {
      console.log("No hay ID de sesión para verificar");
      return;
    }
    
    // Check if this session has already been verified
    if (sessionStorage.getItem(`verified_session_${sid}`)) {
      console.log("Esta sesión ya fue verificada:", sid);
      setVerifiedSessionId(sid);
      setHasPaid(true);
      if (emailAddress && emailAddress.includes('@')) {
        setEmail(emailAddress);
      }
      return;
    }
    
    console.log("Verifying payment session:", sid);
    setIsVerifying(true);
    setSessionId(sid);
    
    // Prevent excessive verification attempts
    setVerificationCount(prev => {
      if (prev >= 3) {
        console.log("Demasiados intentos de verificación, abortando");
        setIsVerifying(false);
        return prev;
      }
      return prev + 1;
    });
    
    try {
      // Asegurarse de que Rewardful esté inicializado antes de proceder
      ensureRewardfulInitialized();
      
      const toastId = toast.loading("Verificando tu pago...");
      const result = await verifyStripePayment(sid, emailAddress);
      toast.dismiss(toastId);
      
      console.log("Payment verification result:", result);
      
      if (result.success) {
        toast.success("¡Pago verificado correctamente!");
        setHasPaid(true);
        setVerifiedSessionId(sid);
        
        // If we have an email from the URL, use it
        if (emailAddress && emailAddress.includes('@')) {
          setEmail(emailAddress);
        }
        
        // Obtener el ID de referido actual para asegurarnos de que se use en la conversión
        const referralId = getReferralId();
        
        // Track conversion in Rewardful if we have an email
        if (emailAddress && emailAddress.includes('@')) {
          // Añadimos más información a la conversión para depuración
          const conversionData = { 
            status: 'payment_complete', 
            sessionId: sid,
            hasReferral: Boolean(referralId),
            referralId: referralId || 'none',
            source: 'payment_verification'
          };
          
          console.log("Enviando datos de conversión a Rewardful:", conversionData);
          
          // Primer intento de conversión inmediato
          trackConversion(emailAddress, conversionData);
          
          // Segunda llamada con un retraso para asegurar el seguimiento
          setTimeout(() => {
            console.log("Enviando datos de conversión a Rewardful (intento secundario)");
            trackConversion(emailAddress, {
              ...conversionData,
              attempt: 'secondary'
            });
          }, 2000);
          
          // Tercer intento adicional con más retraso para garantizar que se registre
          setTimeout(() => {
            console.log("Enviando datos de conversión a Rewardful (intento final)");
            trackConversion(emailAddress, {
              ...conversionData,
              attempt: 'final',
              forceSend: true
            });
          }, 5000);
        }
        
        // Reset referral tracking counters after successful payment
        resetReferralTracking();
        
        // Remove query parameters from URL to prevent confusion on refresh
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } else {
        console.error("Payment verification failed:", result.error || "Unknown error");
        toast.error("No pudimos verificar tu pago. Por favor, contacta a soporte.");
        setPaymentError("No pudimos verificar tu pago. Por favor, contacta a soporte.");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
      toast.error("Error al verificar el pago. Por favor, inténtalo de nuevo o contacta a soporte.");
      setPaymentError("Error al verificar el pago. Por favor, inténtalo de nuevo o contacta a soporte.");
    } finally {
      setIsVerifying(false);
    }
  };
  
  return {
    isVerifying,
    sessionId,
    verifiedSessionId,
    setSessionId,
    verifyPaymentSession
  };
};
